import React, { useState, useEffect } from 'react';
import {
  Container,
  VStack,
  Heading,
  Card,
  CardHeader,
  CardBody,
  Button,
  useToast,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Spinner,
  Center,
  Text,
  Alert,
  AlertIcon,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  Box,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import Articles from './admin/Articles';
import { migrateUsersToPublic, migrateSolvesToTopLevel, fixEmptyNormalizedDisplayNames } from '../utils/userMigrations';
import { useAdminStats } from '../hooks/useAdminStats';
import { AdminUsers } from '../components/AdminUsers';
import { AdminCubes } from '../components/AdminCubes';
import { formatTime } from '../utils/formatTime';
import { PageMeta } from '../components/PageMeta';

const Admin = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { stats, loading, error } = useAdminStats();

  if (loading) {
    return (
      <Center h="50vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          <Text>Failed to load admin statistics: {error.message}</Text>
        </Alert>
      </Container>
    );
  }

  const handleMigration = async (migrationFn: () => Promise<void | number>, successMessage: string) => {
    try {
      const result = await migrationFn();
      toast({
        title: 'Success',
        description: typeof result === 'number' 
          ? `${successMessage} (${result} items processed)`
          : successMessage,
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Migration failed',
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <Box>
      <PageMeta title="admin.pageTitle" description="admin.pageDescription" />
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="stretch">
          <Heading size="lg">{t('admin.statistics')}</Heading>

          {/* Stats Grid */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            {/* Total Users */}
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>{t('admin.stats.totalUsers')}</StatLabel>
                  <StatNumber>{stats?.totalUsers || 0}</StatNumber>
                </Stat>
              </CardBody>
            </Card>

            {/* Active Users */}
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>{t('admin.stats.activeUsers')}</StatLabel>
                  <StatNumber>{stats?.activeUsers || 0}</StatNumber>
                  <StatHelpText>{t('admin.stats.lastMonth')}</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            {/* Total Solves */}
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>{t('admin.stats.totalSolves')}</StatLabel>
                  <StatNumber>{stats?.totalSolves || 0}</StatNumber>
                </Stat>
              </CardBody>
            </Card>

            {/* Average Time */}
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>{t('admin.stats.averageTime')}</StatLabel>
                  <StatNumber>
                    {stats?.averageTime ? `${(stats.averageTime / 1000).toFixed(2)}s` : '-'}
                  </StatNumber>
                </Stat>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Migrations Card */}
          <Card>
            <CardHeader>
              <Heading size="md">Migrations</Heading>
            </CardHeader>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <Button
                  onClick={() => handleMigration(migrateUsersToPublic, 'Users migrated to public successfully')}
                  colorScheme="brand"
                >
                  Migrate Users to Public
                </Button>

                <Button
                  onClick={() => handleMigration(migrateSolvesToTopLevel, 'Solves migrated to top level successfully')}
                  colorScheme="brand"
                >
                  Migrate Solves to Top Level
                </Button>

                <Button
                  onClick={() => handleMigration(fixEmptyNormalizedDisplayNames, 'Empty display names fixed successfully')}
                  colorScheme="brand"
                >
                  Fix Empty Normalized Display Names
                </Button>
              </VStack>
            </CardBody>
          </Card>

          {/* Management Tabs */}
          <Tabs variant="enclosed">
            <TabList>
              <Tab>Users</Tab>
              <Tab>Cubes</Tab>
              <Tab>Articles</Tab>
            </TabList>

            <TabPanels>
              <TabPanel p={0} pt={4}>
                <AdminUsers />
              </TabPanel>
              <TabPanel p={0} pt={4}>
                <AdminCubes />
              </TabPanel>
              <TabPanel p={0} pt={4}>
                <Articles />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </Container>
    </Box>
  );
};

export default Admin; 